import {
  Box,
  Divider,
  Flex,
  Image,
  Text,
  CircularProgress,
  CircularProgressLabel,
  VStack,
  HStack,
} from '@chakra-ui/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { addMinutes } from 'date-fns';
import { QuestionIcon } from '@chakra-ui/icons';
import deviceLayoutImage from '../../../../assets/images/device-layout.png';
import Icon from '../../../../shared/components/Icon';
import { useCampaingCountdown } from '../../../../shared/store/useCampaignCountdown';
import { Item } from '../../../../shared/services/types/campaign';

interface FlagPromotionProps {
  hasBottomSheet?: boolean;
  item?: Partial<Item>;
}

interface PreviewCountdownProps {
  item?: Partial<Item>;
}

interface CircularTimeUnitProps {
  value: number;
  label: string;
  progress: number;
  color: string;
  backgroundColor: string;
}

function CircularTimeUnit({
  value,
  label,
  progress,
  color,
  backgroundColor,
}: CircularTimeUnitProps) {
  return (
    <VStack spacing={2}>
      <CircularProgress
        value={progress}
        size="40px"
        thickness="8px"
        color="#EC008C"
        trackColor={backgroundColor}
      >
        <CircularProgressLabel color={color} fontSize="8px" fontWeight="bold">
          {String(value).padStart(2, '0')}
          <Text
            color={color}
            fontSize="6px"
            fontWeight="600"
            textTransform="uppercase"
            letterSpacing="0.5px"
          >
            {label}
          </Text>
        </CircularProgressLabel>
      </CircularProgress>
    </VStack>
  );
}

function FlagPromotion({ hasBottomSheet, item }: FlagPromotionProps) {
  const { currentDate } = useCampaingCountdown((state) => state);
  const { watch } = useFormContext();

  const initialDate = new Date(currentDate);
  const selectedEndDate = new Date(watch('endDate') || addMinutes(initialDate, 60));

  const diffInMs = selectedEndDate.getTime() - initialDate.getTime();

  const totalSeconds = Math.max(0, Math.floor(diffInMs / 1000));
  const days = Math.floor(totalSeconds / (60 * 60 * 24));
  const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  // Calculate progress percentages for each time unit (countdown style - progress decreases as time runs out)
  const dayProgress =
    days > 0 ? (days / Math.max(1, Math.ceil(totalSeconds / (60 * 60 * 24)))) * 100 : 0;
  const hourProgress = (hours / 24) * 100;
  const minuteProgress = (minutes / 60) * 100;
  const secondProgress = (seconds / 60) * 100;

  const backgroundColor = watch('backgroundColor') || item?.countdown?.backgroundColor || '#F5F5F5';
  const fontColor = watch('fontColor') || item?.countdown?.fontColor || '#FFFFFF';

  return (
    <Box width="100%" bg={backgroundColor} p="1" alignItems="center">
      <HStack spacing={2} pl="4">
        <Text color={fontColor} fontWeight="600" fontSize="8px" textAlign="center" w="max-content">
          {watch('label') ? watch('label') : 'Esta promoção termina em'}
        </Text>

        <HStack spacing={0} align="center">
          <CircularTimeUnit
            value={days}
            label="DIAS"
            progress={dayProgress}
            color={fontColor}
            backgroundColor="#fff"
          />
          <CircularTimeUnit
            value={hours}
            label="HORAS"
            progress={hourProgress}
            color={fontColor}
            backgroundColor="#fff"
          />
          <CircularTimeUnit
            value={minutes}
            label="MIN"
            progress={minuteProgress}
            color={fontColor}
            backgroundColor="#fff"
          />
          <CircularTimeUnit
            value={seconds}
            label="SEG"
            progress={secondProgress}
            color={fontColor}
            backgroundColor="#fff"
          />
        </HStack>

        <Text color={fontColor} fontWeight="600" fontSize="12px" w="max-content">
          {hasBottomSheet ? (
            <Text as="span" ml="0.5rem">
              <QuestionIcon />
            </Text>
          ) : null}
        </Text>
      </HStack>
    </Box>
  );
}

export function PreviewCountdown({ item }: PreviewCountdownProps) {
  const { watch } = useFormContext();

  return (
    <Box
      as="section"
      w="100%"
      h="100%"
      display="flex"
      flexDirection="column"
      alignContent="center"
      justifyContent="center"
    >
      <Flex flexDirection="column" alignItems="center" justifyContent="center">
        <Box
          width="335px"
          height="324px"
          display="flex"
          alignContent="center"
          justifyContent="center"
          flexDirection="column"
        >
          <Text width="400px" fontWeight="700" fontSize="18px" mb="1rem">
            Pré-visualização da contagem regressiva
          </Text>
          <FlagPromotion item={item} hasBottomSheet={watch('hasHelperModal')} />
          {watch('hasHelperModal') || item?.countdown?.helper ? (
            <Flex alignItems="center" justifyContent="center" flexDirection="column" mt="34px">
              <Text width="400px" fontWeight="700" fontSize="18px" mb="1rem" textAlign="center">
                Pré-visualização do modal de ajuda
              </Text>
              <Box position="relative">
                <Image width="284.05px" height="544px" src={deviceLayoutImage} />
                <Box
                  bg="white"
                  position="absolute"
                  bottom="-1px"
                  width="257px"
                  margin="13px"
                  right="1px"
                  rounded="2xl"
                  p="1rem"
                >
                  <Box>
                    <Flex alignItems="center" justifyContent="space-between" mb="1rem">
                      <Box />
                      <Divider width="32px" border="1px solid #D6D5D5" />
                      <Icon icon="icon-close" color="#D6D5D5" />
                    </Flex>

                    <Flex flexDirection="column" alignItems="center" justify="center">
                      <Text
                        as="h2"
                        fontFamily="Pluto"
                        fontSize="15px"
                        width="100%"
                        textAlign="center"
                        mb="10px"
                      >
                        {watch('title-modal')}
                      </Text>
                      <Text
                        as="p"
                        lineHeight="14px"
                        fontSize="13px"
                        width="100%"
                        fontWeight="700"
                        color="#A09E9E"
                        textAlign="center"
                      >
                        {watch('description-modal')}
                      </Text>
                      <Box
                        width="100%"
                        rounded="md"
                        p="10px 16px"
                        background="primary.100"
                        mt="20px"
                        textAlign="center"
                      >
                        <Text fontWeight="800" color="white">
                          {watch('button-text')}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                </Box>
              </Box>
            </Flex>
          ) : (
            <Image src={item?.download} />
          )}
        </Box>
      </Flex>
    </Box>
  );
}
